import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { System, SystemInsert, SystemUpdate } from '@/types/system';
import { toast } from 'sonner';

interface SystemState {
  systems: System[];
  loading: boolean;

  loadSystems: () => Promise<void>;
  addSystem: (system: SystemInsert) => Promise<System | null>;
  updateSystem: (id: string, updates: SystemUpdate) => Promise<void>;
  deleteSystem: (id: string) => Promise<void>;
  getSystemById: (id: string) => System | undefined;
  getSystemsByZone: (zoneId: string) => System[];
}

export const useSystemStore = create<SystemState>()((set, get) => ({
  systems: [],
  loading: false,

  loadSystems: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('systems')
        .select('*')
        .order('name');

      if (error) throw error;
      set({ systems: (data || []) as System[] });
    } catch (error) {
      console.error('Error loading systems:', error);
      toast.error('Failed to load systems');
    } finally {
      set({ loading: false });
    }
  },

  addSystem: async (system) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const { data, error } = await supabase
        .from('systems')
        .insert({ ...system, org_id: profile.org_id })
        .select()
        .single();

      if (error) throw error;

      set(state => ({ systems: [...state.systems, data as System] }));
      toast.success('System created');
      return data as System;
    } catch (error) {
      console.error('Error creating system:', error);
      toast.error('Failed to create system');
      return null;
    }
  },

  updateSystem: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('systems')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        systems: state.systems.map(s => s.id === id ? { ...s, ...updates } : s)
      }));
      toast.success('System updated');
    } catch (error) {
      console.error('Error updating system:', error);
      toast.error('Failed to update system');
    }
  },

  deleteSystem: async (id) => {
    try {
      const { error } = await supabase
        .from('systems')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        systems: state.systems.filter(s => s.id !== id)
      }));
      toast.success('System deleted');
    } catch (error) {
      console.error('Error deleting system:', error);
      toast.error('Failed to delete system');
    }
  },

  getSystemById: (id) => {
    return get().systems.find(s => s.id === id);
  },

  getSystemsByZone: (zoneId) => {
    return get().systems.filter(s => s.zone_id === zoneId);
  },
}));
